import { ActionStatus, ClaimEditValues, ClaimWorkflowStatus } from 'api/endpoints';
import { isNil, memoize } from 'lodash';
import { useClaimResolutionTypeContext } from 'providers/ClaimResolutionTypeProvider';
import { useMemo } from 'react';
import { ISO_DATE, formatDate } from 'utils/formatDate';
import { memoizeEx } from 'utils/lodashEx';

type ClaimEditValuesOpt = Partial<ClaimEditValues>;

export type ResolutionTypeSet = Pick<
  ClaimEditValuesOpt,
  'resolutionTypeId' | 'actionSubStatusId' | 'actionStatus' | 'actionNameId'
>;

interface OptionItem {
  label: string;
  value: string | number;
}

const DEF_UNASSIGNED_ACTIONSTATUS = ActionStatus.ClientReconciled;

/**
 * wrapper around useClaimResolutionTypeContext to create dropdown options
 */
export const useResolutionExhibitTypeOptions = () => {
  const resolutionType = useClaimResolutionTypeContext();

  const {
    resolutionStatusList,
    workflowStatusListDefault,
    resolutionSubstatusList,
    workflowStatusList,
    resolutionNameList,
    resolutionSubstatus,
  } = resolutionType;

  //------ Menu options ----------------------------------------------------
  const defaultWorkflowStatusOptions = useMemo(
    () => workflowStatusListDefault?.map(getWorkflowStatusOption) ?? [],
    [workflowStatusListDefault],
  );

  const resolutionStatusOptions = useMemo(
    (): OptionItem[] =>
      resolutionStatusList.map((r) => ({
        label: `${r.resolutionTypeName}`,
        value: r.resolutionTypeId,
      })),
    [resolutionStatusList],
  );

  const resolutionSubstatusOptions = memoize(
    (statusId: number | undefined): OptionItem[] =>
      resolutionSubstatusList(statusId).map((r) => ({
        label: `${r.actionSubStatusName}`,
        value: r.actionSubStatusId,
      })),
  );

  const workflowStatusOptions = memoizeEx(
    (statusId: number | undefined = 0, subStatusId: number | undefined = 0) =>
      workflowStatusList(statusId, subStatusId).map(getWorkflowStatusOption),
  );

  const resolutionNameOptions = memoizeEx(
    (
      statusId: number | undefined,
      subStatusId: number | undefined,
      options: { skipNone?: boolean } = {},
    ): OptionItem[] =>
      resolutionNameList(statusId, subStatusId, options).map((r) => ({
        label: r.actionName + formatHearingDate(r.hearingDate),
        value: r.id,
      })),
  );

  //------ Change resolution values ----------------------------------------------

  /**
   * Returns the resolution name set based on "Action Status" value.
   *
   * Claims fields "actionTypeId", "actionSubstatusId", "actionStatus" and "actionNameId"
   * are depended on each other and must be updated together
   *
   * function returns the valid "actionSubstatusId", "actionStatus" and "actionNameId"
   * based on the "actionTypeId"
   */
  const getResolutionTypeChangedValues = (
    resolutionTypeId: number | undefined,
    other?: Pick<ResolutionTypeSet, 'actionStatus'>,
  ): ResolutionTypeSet => {
    // lets select the firstSubstatus from the selected status (not sure if it is a good approach though)
    const { actionSubStatusId } = resolutionSubstatusList(resolutionTypeId)[0] ?? {};
    const restValues = getResolutionSubTypeChangedValues(
      resolutionTypeId,
      actionSubStatusId,
      other,
    );

    return { resolutionTypeId, ...restValues };
  };

  /**
   * Returns the resolution name set based on "Action SubStatus" value.
   *
   * Claims fields "resolutionTypeId", "actionSubstatusId", "actionStatus" and "actionNameId"
   * are depended on each other and must be updated together
   *
   * function returns the valid "actionStatus" and "actionNameId"
   * based on the "actionSubstatusId"
   */
  const getResolutionSubTypeChangedValues = (
    resolutionTypeId: number | undefined,
    actionSubStatusId: number,
    other?: Pick<ResolutionTypeSet, 'actionStatus'>,
  ): ResolutionTypeSet => {
    const subStatus = resolutionSubstatus(resolutionTypeId, actionSubStatusId);
    const workflowItems = subStatus?.workflowStatuses ?? [];

    const isActionStatusValid =
      isNil(other?.actionStatus) ||
      workflowItems.some((r) => r.actionStatus === other?.actionStatus);

    const actionStatus = isActionStatusValid
      ? other?.actionStatus
      : DEF_UNASSIGNED_ACTIONSTATUS;

    const actionNameId = 0;

    return {
      actionSubStatusId,
      actionStatus,
      actionNameId,
    };
  };

  //------------------------------------------------------------------------------

  return {
    defaultWorkflowStatusOptions,
    resolutionStatusOptions,
    resolutionSubstatusOptions,
    workflowStatusOptions,
    resolutionNameOptions,
    getResolutionTypeChangedValues,
    getResolutionSubTypeChangedValues,
  };
};

const formatHearingDate = (date: string | null | undefined) =>
  date ? ` [${formatDate(date, ISO_DATE)}]` : '';

const getWorkflowStatusOption = (r: ClaimWorkflowStatus): OptionItem => ({
  label: `${r.actionStatusDisplayName}`,
  value: r.actionStatus,
});
